import React, { useEffect, useState, useCallback } from 'react';
import { financeiroAPI } from '../../lib/api';
import { useModulosAtivos } from '../../hooks/useModulosAtivos';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Wallet, TrendingUp, TrendingDown, AlertTriangle, RefreshCw,
  Scale, ArrowRight,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const STATUS_LABEL = {
  em_andamento: 'Em Andamento', ativo: 'Em Andamento',
  pausado: 'Pausado', aguardando_cliente: 'Aguardando Cliente',
  aguardando_pagamento: 'Aguardando Pagamento',
  concluido: 'Concluído', cancelado: 'Cancelado',
};

/**
 * Card de totalizador clicável.
 */
function StatCard({ icon: Icon, label, value, hint, color, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full bg-card border-l-4 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all active:scale-[0.99]"
      style={{ borderLeftColor: color }}
      data-testid={testid}
    >
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide font-medium" style={{ color }}>{label}</p>
            <p className="text-2xl font-bold mt-1 truncate">{typeof value === 'number' ? fmt(value) : value}</p>
            {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
          </div>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}1f` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
        <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1 opacity-70">
          <ArrowRight className="w-3 h-3" /> ver serviços
        </div>
      </div>
    </button>
  );
}

const TIPO_INFO = {
  faturamento:    { titulo: 'Faturamento · todos os serviços movimentados', color: '#0891b2' },
  despesa_total:  { titulo: 'Despesas Totais · todas as saídas registradas', color: '#e11d48' },
  lucro:          { titulo: 'Lucro · faturamento líquido de despesas',       color: '#16a34a' },
  receita:        { titulo: 'Recebimentos · parcelas pagas',                 color: '#16a34a' },
  despesa_pagas:  { titulo: 'Pagamentos · despesas de serviços já pagos',    color: '#e11d48' },
  saldo:          { titulo: 'Saldo · entradas menos saídas efetivas',        color: '#2563eb' },
  a_receber:      { titulo: 'A Receber · parcelas pendentes',                color: '#d97706' },
  a_pagar:        { titulo: 'A Pagar · despesas de serviços pendentes',      color: '#d97706' },
};

export default function FinanceiroDashboard() {
  const { financeiroAtivo, loading: loadingMod } = useModulosAtivos();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState({ inicio: '', fim: '' });
  const [detalheTipo, setDetalheTipo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (periodo.inicio) params.data_inicio = periodo.inicio;
      if (periodo.fim) params.data_fim = periodo.fim;
      const res = await financeiroAPI.dashboard(params);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [periodo.inicio, periodo.fim]);

  useEffect(() => { if (financeiroAtivo) load(); }, [financeiroAtivo, load]);

  // Redireciona se módulo está desativado
  const moduloInativo = !loadingMod && financeiroAtivo === false;
  useEffect(() => {
    if (moduloInativo) navigate('/', { replace: true });
  }, [moduloInativo, navigate]);

  if (loadingMod) return <div className="p-10 text-center text-muted-foreground">Carregando módulo...</div>;
  if (!financeiroAtivo) return null;
  if (loading && !data) return <div className="p-10 text-center text-muted-foreground">Carregando dashboard...</div>;
  if (!data) return null;

  const t = data.totais;
  const periodoLabel = data.periodo?.acumulado
    ? 'Acumulado (todo o histórico)'
    : `${periodo.inicio || '—'} até ${periodo.fim || '—'}`;

  const detalheLista = detalheTipo
    ? (
        detalheTipo === 'saldo' || detalheTipo === 'receita' ? data.detalhes.receita :
        detalheTipo === 'a_receber' ? data.detalhes.pendente :
        detalheTipo === 'a_pagar' || detalheTipo === 'despesa_pagas' || detalheTipo === 'despesa_total' ? data.detalhes.despesa :
        detalheTipo === 'lucro' || detalheTipo === 'faturamento' ? data.detalhes.totalizado :
        []
      )
    : [];

  return (
    <div className="space-y-5" data-testid="financeiro-dashboard-v2">
      {/* Cabeçalho + período */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard Financeiro</h1>
          <p className="text-sm text-muted-foreground">{periodoLabel}</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-[11px]">De</Label>
            <Input type="date" value={periodo.inicio} onChange={(e) => setPeriodo(p => ({ ...p, inicio: e.target.value }))} className="h-9" />
          </div>
          <div>
            <Label className="text-[11px]">Até</Label>
            <Input type="date" value={periodo.fim} onChange={(e) => setPeriodo(p => ({ ...p, fim: e.target.value }))} className="h-9" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setPeriodo({ inicio: '', fim: '' })} data-testid="btn-limpar-periodo">
            Acumulado
          </Button>
          <Button onClick={load} size="sm" data-testid="btn-atualizar">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Atualizar
          </Button>
        </div>
      </div>

      {/* ===== Grid principal: 3 cards à esquerda em 2 linhas + card lateral A Receber/A Pagar ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Coluna esquerda: 9 col, 2 linhas */}
        <div className="lg:col-span-9 space-y-4">
          {/* Linha 1 — Resumo Geral */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              icon={Wallet}
              label="Faturamento"
              value={t.faturamento}
              hint="Pagas + Pendentes + Despesas"
              color="#0891b2"
              onClick={() => setDetalheTipo('faturamento')}
              testid="card-faturamento"
            />
            <StatCard
              icon={TrendingDown}
              label="Despesas Totais"
              value={t.despesa}
              hint="Saídas registradas"
              color="#e11d48"
              onClick={() => setDetalheTipo('despesa_total')}
              testid="card-despesa-total"
            />
            <StatCard
              icon={TrendingUp}
              label="Lucro"
              value={t.lucro}
              hint="Faturamento − Despesas"
              color="#16a34a"
              onClick={() => setDetalheTipo('lucro')}
              testid="card-lucro"
            />
          </div>

          {/* Linha 2 — Fluxo de Caixa */}
          <div>
            <h2 className="text-base font-semibold text-muted-foreground mb-2">Fluxo de Caixa</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                icon={TrendingUp}
                label="Recebimentos"
                value={t.receita}
                hint="Parcelas pagas (entrada efetiva)"
                color="#16a34a"
                onClick={() => setDetalheTipo('receita')}
                testid="card-recebimentos"
              />
              <StatCard
                icon={TrendingDown}
                label="Pagamentos"
                value={t.despesa_pagas}
                hint="Despesas de serviços pagos/concluídos"
                color="#e11d48"
                onClick={() => setDetalheTipo('despesa_pagas')}
                testid="card-pagamentos"
              />
              <StatCard
                icon={Scale}
                label="Saldo"
                value={t.saldo}
                hint="Recebimentos − Pagamentos"
                color="#2563eb"
                onClick={() => setDetalheTipo('saldo')}
                testid="card-saldo"
              />
            </div>
          </div>
        </div>

        {/* Coluna direita: card grande lateral (A Receber + A Pagar) */}
        <div className="lg:col-span-3">
          <div
            className="bg-card border-l-4 rounded-lg shadow-sm h-full flex flex-col divide-y"
            style={{ borderLeftColor: '#d97706' }}
          >
            {/* A Receber */}
            <button
              type="button"
              onClick={() => setDetalheTipo('a_receber')}
              className="flex-1 text-left p-5 hover:bg-amber-50/40 transition-colors active:scale-[0.99]"
              data-testid="card-a-receber"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide font-medium text-amber-700">A Receber</p>
                  <p className="text-2xl font-bold mt-2 truncate">{fmt(t.pendente)}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Parcelas pendentes</p>
                </div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-100">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1 opacity-70">
                <ArrowRight className="w-3 h-3" /> ver serviços
              </div>
            </button>

            {/* A Pagar */}
            <button
              type="button"
              onClick={() => setDetalheTipo('a_pagar')}
              className="flex-1 text-left p-5 hover:bg-rose-50/40 transition-colors active:scale-[0.99]"
              data-testid="card-a-pagar"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide font-medium text-rose-700">A Pagar</p>
                  <p className="text-2xl font-bold mt-2 truncate">{fmt(t.a_pagar)}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Despesas de serviços pendentes</p>
                </div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-rose-100">
                  <TrendingDown className="w-5 h-5 text-rose-600" />
                </div>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1 opacity-70">
                <ArrowRight className="w-3 h-3" /> ver serviços
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Alerta de parcelas vencidas */}
      {data.vencidas?.quantidade > 0 && (
        <Card className="border-amber-300 bg-amber-50/50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900 text-sm">
                {data.vencidas.quantidade} parcela(s) vencida(s) — {fmt(data.vencidas.valor)}
              </p>
              <p className="text-xs text-amber-700">Acesse a página Serviços para regularizar.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate('/financeiro/servicos')}>
              Ver serviços
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-muted-foreground uppercase mb-3">Receita × Despesa · últimos 6 meses</p>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={data.grafico_mensal}>
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$ ${v/1000}k`} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                  <Bar dataKey="receita" fill="#16a34a" name="Receita" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="despesa" fill="#e11d48" name="Despesa" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-muted-foreground uppercase mb-3">Top despesas por categoria</p>
            {data.top_categorias_despesa.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Nenhuma despesa no período</p>
            ) : (
              <ul className="space-y-2">
                {data.top_categorias_despesa.map(c => (
                  <li key={c.categoria_id} className="flex items-center justify-between text-sm border-b last:border-b-0 pb-2 last:pb-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.cor }} />
                      <span className="truncate">{c.nome}</span>
                    </div>
                    <span className="font-semibold text-rose-600">{fmt(c.valor)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cards de resumo de serviços */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Total Serviços</p>
          <p className="text-2xl font-bold">{data.servicos.total}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Ativos</p>
          <p className="text-2xl font-bold text-emerald-600">{data.servicos.ativos}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-[11px] text-muted-foreground uppercase">Concluídos</p>
          <p className="text-2xl font-bold text-sky-600">{data.servicos.concluidos}</p>
        </CardContent></Card>
      </div>

      {/* Dialog de detalhe ao clicar nos cards */}
      <Dialog open={!!detalheTipo} onOpenChange={(v) => { if (!v) setDetalheTipo(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="dialog-detalhe-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ background: TIPO_INFO[detalheTipo]?.color }}
              />
              {TIPO_INFO[detalheTipo]?.titulo}
            </DialogTitle>
          </DialogHeader>
          {detalheLista.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Nenhum serviço relacionado neste período.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase">
                <tr>
                  <th className="text-left p-2">Cliente</th>
                  <th className="text-left p-2">Serviço</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-right p-2">Valor</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {detalheLista.map(d => (
                  <tr key={d.id || d.titulo} className="border-t hover:bg-muted/20">
                    <td className="p-2 font-medium truncate max-w-[180px]">{d.cliente_nome || '—'}</td>
                    <td className="p-2 truncate max-w-[260px]">{d.titulo || '(sem título)'}</td>
                    <td className="p-2 text-xs text-muted-foreground">{STATUS_LABEL[d.status] || d.status || '—'}</td>
                    <td className="p-2 text-right font-semibold">{fmt(d.valor)}</td>
                    <td className="p-2 text-center">
                      {d.id && (
                        <button
                          onClick={() => { setDetalheTipo(null); navigate(`/financeiro/servicos?svc=${d.id}`); }}
                          title="Abrir serviço"
                          data-testid={`detalhe-go-${d.id}`}
                          className="text-cyan-600 hover:text-cyan-800"
                        >
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/60 font-bold border-t-2">
                <tr>
                  <td colSpan={3} className="p-2 text-right uppercase text-xs">Total</td>
                  <td className="p-2 text-right">
                    {fmt(detalheLista.reduce((s, d) => s + (d.valor || 0), 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
